import React, { useEffect, useRef, useState } from "react";
import { FiX, FiChevronDown, FiGlobe } from "react-icons/fi";
import { COUNTRIES, countryName } from "../../../utils/countries";

/**
 * Which countries a task is shown in.
 *
 * Nothing selected is the common case and means global — every user, in every
 * country — so the empty state says so rather than looking like a field that
 * was forgotten. The look follows the notification page's country picker, so
 * the two targeting controls read as the same thing.
 */
const CountryPicker = ({ value = [], onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const toggle = (code) =>
    onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code].sort());

  const term = search.trim().toLowerCase();

  // Exact code or name first, then names that start with the search, then the
  // rest — so "india" puts India above British Indian Ocean Territory, and
  // Enter picks what was meant.
  const rank = (c) => {
    const name = c.name.toLowerCase();
    if (c.code.toLowerCase() === term || name === term) return 0;
    return name.startsWith(term) ? 1 : 2;
  };
  const options = COUNTRIES.filter(
    (c) =>
      !value.includes(c.code) &&
      (!term || c.name.toLowerCase().includes(term) || c.code.toLowerCase().includes(term))
  ).sort((a, b) => (term ? rank(a) - rank(b) : 0));

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Enter picks the top match instead of submitting the whole form.
      e.preventDefault();
      if (term && options.length > 0) {
        toggle(options[0].code);
        setSearch("");
      }
    } else if (e.key === "Backspace" && !search && value.length > 0) {
      toggle(value[value.length - 1]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setSearch("");
    }
  };

  const names = value.map(countryName);

  return (
    <div ref={ref} className="relative">
      <div
        className={`flex flex-wrap items-center gap-1.5 px-3 py-2 border rounded-lg min-h-[40px] cursor-text transition ${
          open ? "border-blue-400 ring-2 ring-blue-500/20" : "border-gray-200"
        } ${disabled ? "bg-gray-50 pointer-events-none" : ""}`}
        onClick={() => setOpen(true)}
      >
        {value.length === 0 && !open && (
          <span className="flex items-center gap-1 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded">
            <FiGlobe size={11} /> Global
          </span>
        )}
        {value.map((code) => (
          <span
            key={code}
            className="flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded"
          >
            <span className="font-mono">{code}</span>
            <span className="text-blue-500 font-normal hidden sm:inline">{countryName(code)}</span>
            <button
              type="button"
              aria-label={`Remove ${countryName(code)}`}
              onClick={(e) => {
                e.stopPropagation();
                toggle(code);
              }}
              className="ml-0.5 hover:text-red-500 transition"
            >
              <FiX size={10} />
            </button>
          </span>
        ))}
        <div className="flex items-center gap-1 flex-1 min-w-[120px]">
          <input
            aria-label="Countries"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={value.length === 0 ? "Search to limit to countries..." : "Add more..."}
            className="text-sm outline-none flex-1 bg-transparent"
            disabled={disabled}
          />
          <FiChevronDown
            size={14}
            className={`text-gray-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      {open && (
        <div
          role="listbox"
          aria-label="Country options"
          className="absolute z-30 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg w-full sm:w-80 max-h-56 overflow-y-auto"
        >
          {options.length === 0 ? (
            <div className="px-4 py-3 text-xs text-gray-400 text-center">
              {term ? "No countries match your search" : "All countries selected"}
            </div>
          ) : (
            options.map((c) => (
              <button
                key={c.code}
                type="button"
                role="option"
                aria-selected={false}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  toggle(c.code);
                  setSearch("");
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2.5 transition"
              >
                <span className="font-mono text-xs text-gray-400 w-7 flex-shrink-0">{c.code}</span>
                <span className="text-gray-700 flex-1">{c.name}</span>
              </button>
            ))
          )}
        </div>
      )}

      <div className="flex items-start justify-between gap-2 mt-1">
        <p className="text-[11px] text-gray-400">
          {value.length === 0
            ? "Shown to every user, in every country. Pick countries to limit it."
            : `Shown only to users whose profile country is ${
                names.length === 1 ? names[0] : `one of these ${names.length}`
              }. Users with no country set will not see it.`}
        </p>
        {value.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[11px] font-semibold text-blue-600 hover:underline whitespace-nowrap"
          >
            Make global
          </button>
        )}
      </div>
    </div>
  );
};

export default CountryPicker;
